import React, { useMemo, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt, FaPen, FaTrash } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import ReminderEditDialog from './ReminderEditDialog';
import styles from '../styles/reminders/ReminderCalendar.module.css';

const LOCALE_MAP = { de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT' };

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function toKey(d) {
  const x = startOfDay(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}
/** Woche beginnt mit Montag (EU-üblich), unabhängig von der Sprache. */
function startOfWeek(d) {
  const x = startOfDay(d);
  const day = x.getDay(); // 0 = Sonntag .. 6 = Samstag
  const diff = (day === 0 ? -6 : 1) - day;
  return addDays(x, diff);
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function formatDomain(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

/**
 * Kalender-Übersicht der Erinnerungen (Monats-/Wochenansicht).
 * Jede Erinnerung wird an ihrem nächsten Fälligkeitstermin
 * (snoozedUntil bzw. nextAt) einsortiert - Wiederholungen zeigen
 * also stets nur ihren jeweils nächsten Termin, keine Serie.
 */
export default function ReminderCalendar({ items, onToggle, onDelete, onChanged }) {
  const { language, t } = useLanguage();
  const locale = LOCALE_MAP[language] || 'de-DE';

  const [mode, setMode] = useState('month');
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [editingReminder, setEditingReminder] = useState(null);

  const today = startOfDay(new Date());

  const byDate = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const due = r.snoozedUntil || r.nextAt;
      if (!due) continue;
      const key = toKey(new Date(due));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    for (const list of map.values()) {
      list.sort((a, b) => new Date(a.snoozedUntil || a.nextAt) - new Date(b.snoozedUntil || b.nextAt));
    }
    return map;
  }, [items]);

  const weeks = useMemo(() => {
    if (mode === 'week') {
      const start = startOfWeek(cursor);
      return [Array.from({ length: 7 }, (_, i) => addDays(start, i))];
    }
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const gridStart = startOfWeek(first);
    const dayCount = Math.round((last - gridStart) / 86400000) + 1;
    const totalCells = Math.ceil(dayCount / 7) * 7;
    const days = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));
    const rows = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return rows;
  }, [mode, cursor]);

  const weekdayLabels = useMemo(() => {
    const start = startOfWeek(new Date());
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(addDays(start, i)));
  }, [locale]);

  const headerLabel = useMemo(() => {
    if (mode === 'month') {
      return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(cursor);
    }
    const start = startOfWeek(cursor);
    const end = addDays(start, 6);
    const df = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' });
    const yf = new Intl.DateTimeFormat(locale, { year: 'numeric' });
    return `${df.format(start)} – ${df.format(end)} ${yf.format(end)}`;
  }, [mode, cursor, locale]);

  const dayHeadingFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    [locale]
  );
  const timeFmt = useMemo(() => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }), [locale]);

  const freqLabels = {
    once: t('reminderOnce') || 'Einmalig',
    daily: t('reminderDaily') || 'Täglich',
    weekly: t('reminderWeekly') || 'Wöchentlich',
    monthly: t('reminderMonthly') || 'Monatlich',
    custom: t('reminders.frequencyCustom') || 'Individuell',
  };

  const goPrev = () =>
    setCursor((prev) => (mode === 'month' ? new Date(prev.getFullYear(), prev.getMonth() - 1, 1) : addDays(prev, -7)));
  const goNext = () =>
    setCursor((prev) => (mode === 'month' ? new Date(prev.getFullYear(), prev.getMonth() + 1, 1) : addDays(prev, 7)));
  const goToday = () => {
    setCursor(startOfDay(new Date()));
    setSelectedDay(startOfDay(new Date()));
  };

  const selectDay = (day) => {
    setSelectedDay(day);
    if (mode === 'month' && (day.getMonth() !== cursor.getMonth() || day.getFullYear() !== cursor.getFullYear())) {
      setCursor(new Date(day.getFullYear(), day.getMonth(), 1));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm(t('reminders.deleteConfirm') || 'Diese Erinnerung wirklich löschen?')) {
      onDelete(id);
    }
  };

  const selectedItems = byDate.get(toKey(selectedDay)) || [];
  const todayKey = toKey(today);
  const selectedKey = toKey(selectedDay);

  return (
    <div className={styles.calendar}>
      {/* Toolbar: Monat/Woche-Umschalter + Navigation */}
      <div className={styles.toolbar}>
        <div className={styles.modeSwitch} role="tablist" aria-label={t('reminders.viewMode') || 'Ansicht'}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'month'}
            className={`${styles.modeBtn} ${mode === 'month' ? styles.modeBtnActive : ''}`}
            onClick={() => setMode('month')}
          >
            {t('reminders.month') || 'Monat'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'week'}
            className={`${styles.modeBtn} ${mode === 'week' ? styles.modeBtnActive : ''}`}
            onClick={() => setMode('week')}
          >
            {t('reminders.week') || 'Woche'}
          </button>
        </div>

        <div className={styles.nav}>
          <button type="button" className={styles.navBtn} onClick={goPrev} aria-label={t('reminders.previous') || 'Zurück'}>
            <FaChevronLeft />
          </button>
          <button type="button" className={styles.todayBtn} onClick={goToday}>
            {t('reminders.today') || 'Heute'}
          </button>
          <button type="button" className={styles.navBtn} onClick={goNext} aria-label={t('reminders.next') || 'Weiter'}>
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className={styles.periodLabel}>{headerLabel}</div>

      {/* Wochentags-Kopfzeile */}
      <div className={styles.weekdayRow}>
        {weekdayLabels.map((w, i) => (
          <div key={i} className={styles.weekdayCell}>
            {w}
          </div>
        ))}
      </div>

      {/* Tage-Grid */}
      <div className={styles.grid}>
        {weeks.map((week, wi) => (
          <div key={wi} className={styles.gridRow}>
            {week.map((day) => {
              const key = toKey(day);
              const dayItems = byDate.get(key) || [];
              const isToday = key === todayKey;
              const isSelected = key === selectedKey;
              const otherMonth = mode === 'month' && day.getMonth() !== cursor.getMonth();
              return (
                <button
                  type="button"
                  key={key}
                  className={[
                    styles.dayCell,
                    isToday ? styles.dayCellToday : '',
                    isSelected ? styles.dayCellSelected : '',
                    otherMonth ? styles.dayCellOther : '',
                  ]
                    .join(' ')
                    .trim()}
                  onClick={() => selectDay(day)}
                >
                  <span className={styles.dayNumber}>{day.getDate()}</span>
                  {dayItems.length > 0 && (
                    <span className={styles.dots} aria-hidden="true">
                      {dayItems.slice(0, 3).map((r) => (
                        <span key={r.id} className={`${styles.dot} ${!r.active ? styles.dotInactive : ''}`} />
                      ))}
                      {dayItems.length > 3 && <span className={styles.dotMore}>+{dayItems.length - 3}</span>}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Tages-Details */}
      <div className={styles.dayDetails}>
        <h3 className={styles.dayDetailsHeading}>{dayHeadingFmt.format(selectedDay)}</h3>

        {selectedItems.length === 0 ? (
          <p className={styles.emptyDay}>{t('reminders.noRemindersThisDay') || 'Keine Erinnerungen an diesem Tag'}</p>
        ) : (
          <ul className={styles.itemList}>
            {selectedItems.map((r) => {
              const hasBookmark = Boolean(r.bookmarkUrl);
              const time = timeFmt.format(new Date(r.snoozedUntil || r.nextAt));
              return (
                <li key={r.id} className={`${styles.item} ${!r.active ? styles.itemInactive : ''}`}>
                  <span className={styles.itemIcon} aria-hidden="true">
                    {hasBookmark ? '🔖' : r.icon || '⏰'}
                  </span>
                  <div className={styles.itemBody}>
                    <span className={styles.itemTitle}>{r.title}</span>
                    <span className={styles.itemMeta}>
                      <span className={styles.itemTime}>{time}</span>
                      <span className={styles.itemDot}>•</span>
                      <span className={styles.itemFreq}>{freqLabels[r.frequency] || r.frequency}</span>
                      {hasBookmark && (
                        <>
                          <span className={styles.itemDot}>•</span>
                          <span className={styles.itemDomain}>{formatDomain(r.bookmarkUrl)}</span>
                        </>
                      )}
                    </span>
                  </div>
                  <div className={styles.itemActions}>
                    {hasBookmark && (
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title={t('reminders.openBookmark') || 'Lesezeichen öffnen'}
                        onClick={() => window.open(r.bookmarkUrl, '_blank', 'noopener,noreferrer')}
                      >
                        <FaExternalLinkAlt />
                      </button>
                    )}
                    <label
                      className={styles.toggle}
                      title={r.active ? (t('reminders.deactivate') || 'Deaktivieren') : (t('reminders.activate') || 'Aktivieren')}
                    >
                      <input type="checkbox" checked={r.active} onChange={(e) => onToggle(r.id, e.target.checked)} />
                      <span className={styles.toggleSlider} aria-hidden="true" />
                    </label>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      title={t('reminders.editAction') || 'Bearbeiten'}
                      onClick={() => setEditingReminder(r)}
                    >
                      <FaPen />
                    </button>
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      title={t('reminders.deleteAction') || 'Löschen'}
                      onClick={() => handleDelete(r.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editingReminder && (
        <ReminderEditDialog
          reminder={editingReminder}
          onClose={() => setEditingReminder(null)}
          onSaved={() => {
            setEditingReminder(null);
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
}
